import { useState } from 'react';
import { Plus, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '../lib/api';

/**
 * CategoryPickerWithAdd — a native <select> + "+ New category" button.
 *
 * The user requested (Sprint 2026-07): "I can't add my own. I want my own list."
 * Backend already supports custom categories via POST /api/expense-categories;
 * previously the frontend only surfaced the preset list as a plain <select>
 * with no way to append. This component fixes that by rendering a compact
 * "+ Add" chip next to the picker that expands into a tiny inline form.
 *
 * Props:
 *   value          - currently selected category_key
 *   onChange(key)  - fires when the user picks a different category
 *   categories     - array of { key, label, icon, is_preset, default_kind }
 *   onCategoriesChange(newList) - called after a new category is created so
 *                                 the parent can update its list
 *   testId         - base data-testid for the select input
 *   size           - 'sm' | 'md' (default 'md')
 *   allowDelete    - if true, show a delete-x on non-preset categories
 */
export function CategoryPickerWithAdd({
    value,
    onChange,
    categories = [],
    onCategoriesChange,
    testId = 'category-picker',
    size = 'md',
    placeholder = 'Select category…',
    allowDelete = false,
}) {
    const [adding, setAdding] = useState(false);
    const [newLabel, setNewLabel] = useState('');
    const [saving, setSaving] = useState(false);

    const heightClass = size === 'sm' ? 'h-8 text-xs' : 'h-9 text-sm';

    async function saveNew() {
        const label = newLabel.trim();
        if (!label) {
            toast.error('Give the new category a name');
            return;
        }
        if (label.length > 40) {
            toast.error('Keep the category name under 40 characters');
            return;
        }
        setSaving(true);
        try {
            const resp = await api.addExpenseCategory({ label });
            const created = resp?.category || resp;
            const newList = Array.isArray(categories)
                ? [...categories, created]
                : [created];
            onCategoriesChange?.(newList);
            onChange?.(created.key);
            setNewLabel('');
            setAdding(false);
            toast.success(`Added "${created.label}"`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not add category');
        } finally {
            setSaving(false);
        }
    }

    async function removeCategory(key, label) {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete category "${label}"? Existing expenses tagged to it will be moved to "Other".`)) {
            return;
        }
        try {
            await api.removeExpenseCategory(key);
            const newList = (categories || []).filter((c) => c.key !== key);
            onCategoriesChange?.(newList);
            if (value === key) onChange?.('');
            toast.success(`Deleted "${label}"`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not delete category');
        }
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <select
                    value={value || ''}
                    onChange={(e) => onChange?.(e.target.value)}
                    className={`flex-1 rounded-md border border-input bg-background px-3 ${heightClass}`}
                    data-testid={testId}
                >
                    <option value="">{placeholder}</option>
                    {(categories || []).map((c) => (
                        <option key={c.key} value={c.key}>
                            {c.label}{!c.is_preset ? ' · custom' : ''}
                        </option>
                    ))}
                </select>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAdding((x) => !x)}
                    className={`${heightClass} px-2 shrink-0`}
                    data-testid={`${testId}-add-toggle`}
                    title={adding ? 'Cancel adding' : 'Add a new category'}
                >
                    {adding ? <X size={14} /> : <Plus size={14} />}
                    <span className="ml-1 hidden sm:inline">{adding ? 'Cancel' : 'New'}</span>
                </Button>
            </div>

            {adding && (
                <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 p-2 space-y-2">
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Your custom category
                    </Label>
                    <div className="flex items-center gap-2">
                        <Input
                            autoFocus
                            value={newLabel}
                            onChange={(e) => setNewLabel(e.target.value)}
                            placeholder="e.g. Packaging Material"
                            className={heightClass}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    saveNew();
                                }
                                if (e.key === 'Escape') setAdding(false);
                            }}
                            maxLength={40}
                            data-testid={`${testId}-new-label`}
                        />
                        <Button
                            type="button"
                            size="sm"
                            onClick={saveNew}
                            disabled={saving || !newLabel.trim()}
                            className={heightClass}
                            data-testid={`${testId}-save-new`}
                        >
                            {saving ? <Loader2 size={13} className="animate-spin" /> : 'Save'}
                        </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-tight">
                        Only you see this. It shows up alongside the built-in categories everywhere in the app.
                    </p>
                </div>
            )}

            {allowDelete && (categories || []).some((c) => !c.is_preset) && (
                <div className="flex flex-wrap gap-1 pt-1">
                    {(categories || [])
                        .filter((c) => !c.is_preset)
                        .map((c) => (
                            <button
                                type="button"
                                key={c.key}
                                onClick={() => removeCategory(c.key, c.label)}
                                className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-muted/50 hover:bg-destructive/10 hover:border-destructive/50 hover:text-destructive px-2 py-0.5 text-[11px] transition-colors"
                                data-testid={`${testId}-delete-${c.key}`}
                                title={`Delete "${c.label}"`}
                            >
                                {c.label}
                                <X size={11} />
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
}

export default CategoryPickerWithAdd;
